import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

const principalOf = (state) => state.isAuthenticated
  ? `${state.user?.id ?? ''}:${state.activeMembership?.tenant?.public_id ?? ''}`
  : null;

/**
 * Auth store shape:
 *   accessToken: string | null
 *   refreshToken: string | null
 *   user: { id, email, name } | null
 *   activeMembership: { tenant: { public_id, name, slug, plan }, role, joined_at } | null
 *   memberships: TenantMembership[]  — NOT persisted; loaded from /me each time the console mounts
 *   features: { principal, keys }  — NOT persisted; the flags /me names as on, and for whom
 *   isAuthenticated: boolean
 */
const useAuthStore = create(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      activeMembership: null,
      memberships: [],
      features: { principal: null, keys: [] },
      isAuthenticated: false,

      // A new session starts with its own membership only, never the tenant
      // list of the session it replaces; the console then loads the full list.
      setAuth: ({ accessToken, refreshToken, user, membership }) =>
        set({
          accessToken,
          refreshToken,
          user,
          activeMembership: membership,
          memberships: [membership],
          isAuthenticated: true,
        }),

      setMemberships: (memberships) => set({ memberships }),

      updateUser: (user) => set((state) => ({ user: { ...state.user, ...user } })),

      setFeatures: (principal, keys) => set({ features: { principal, keys } }),

      setActiveMembership: (membership) => set({ activeMembership: membership }),

      updateTokens: ({ accessToken, refreshToken }) =>
        set({ accessToken, refreshToken }),

      logout: () =>
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          activeMembership: null,
          memberships: [],
          features: { principal: null, keys: [] },
          isAuthenticated: false,
        }),
    }),
    {
      name: 'evolve-auth',
      storage: createJSONStorage(() => localStorage),
      // memberships[] is never persisted — the console reloads it from /me.
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        activeMembership: state.activeMembership,
        isAuthenticated: state.isAuthenticated,
      }),
      merge: (persisted, current) => {
        const next = { ...current, ...persisted };
        if (principalOf(next) !== principalOf(current)) {
          next.memberships = next.isAuthenticated && next.activeMembership ? [next.activeMembership] : [];
          next.features = { principal: null, keys: [] };
        }
        return next;
      },
    }
  )
);

/**
 * Who the console acts as — user × active tenant, null when signed out.
 * Nothing loaded as one principal may be shown to another: the query cache
 * (api/queryClient) and the console subtree (ProtectedRoute) are keyed on it.
 */
export const selectPrincipal = principalOf;

/**
 * Is feature flag *key* on for the current principal? False until /me has
 * answered for exactly this principal, so a switched tenant never inherits the
 * previous one's features. Only UX: the backend enforces every gate itself.
 */
export const selectFeature = (key) => (state) =>
  state.features.principal === selectPrincipal(state) && state.features.keys.includes(key);

// Tabs share the persisted session. A password rotation or logout in one tab
// must reach the others before they try to refresh a revoked credential.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === 'evolve-auth') useAuthStore.persist.rehydrate();
  });
}

export default useAuthStore;
